import { cn } from "../../../utils";
import { Tooltip, Button, Modal } from "@gw/web-basic-components";
import { produce } from "immer";
import { VALUE_TYPE } from "../../contanst";
import { getLoopMeta, isListValue } from "../../utils";
import React, { useEffect, useRef, useState } from "react";
import useFormModal from "../../hooks/use-form-modal";
import { TableEditorConfig, TableRenderConfig, Value } from "../../dto";
import { useLatest } from "ahooks";

const actionBtnClassName =
  "!wbuc-absolute wbuc-top-0 wbuc-size-8 !wbuc-min-w-0 !wbuc-p-0 wbuc-text-center wbuc-leading-8 [pointer-events:all]";

export interface LoopActionConfig {
  add: { loopId: string; top: number }[];
  del: { rowIds: string[]; loopId: string; top: number; idx: number }[];
}

export interface LoopActionProps {
  enabled?: boolean;
  dataSource: Value[];
  tableConfig: TableEditorConfig;
  renderConfig: TableRenderConfig;
  getContainer?: () => HTMLElement;
  onChange?: (cfg: LoopActionConfig) => void;
  onDataSourceChange?: (
    type: "add" | "remove",
    cb: (prev: Value[], cb: (code: string, value?: any) => void) => Value[]
  ) => void;
}

const LoopAction = ({
  enabled,
  dataSource,
  tableConfig,
  renderConfig,
  getContainer,
  onChange,
  onDataSourceChange,
}: LoopActionProps) => {
  // 循环操作配置
  const [config, setConfig] = useState<LoopActionConfig>({
    add: [],
    del: [],
  });
  const actionRef = useRef<HTMLDivElement>();
  const [modal, open] = useFormModal({ title: "新增", width: "30rem" });
  // 容器节点
  const containerRef = useRef<HTMLElement>();
  containerRef.current = getContainer?.() ?? document.body;

  // 获取行中心位置
  const getCenterByRowIds = (ids: string[]) => {
    const container = containerRef.current;
    const { top } = container.getBoundingClientRect();
    const startId = ids[0],
      endId = ids[ids.length - 1];
    const startRow = container.querySelector(
      `[data-id="${startId}"]`
    ) as HTMLTableRowElement;
    const endRow = container.querySelector(
      `[data-id="${endId}"]`
    ) as HTMLTableRowElement;
    const { top: startTop, height: startHeight } =
      startRow.getBoundingClientRect();
    const relativeStartTop = startTop - top;
    if (startId === endId) {
      if (startHeight === 0) {
        return relativeStartTop;
      }
      return relativeStartTop + startHeight / 2;
    } else {
      const { top: endTop, height: endHeight } = endRow.getBoundingClientRect();
      return relativeStartTop + (endTop + endHeight - startTop) / 2;
    }
  };
  const generateGonfig = () => {
    const addRows = tableConfig.loops
      .map((loop) =>
        renderConfig.rows.filter(
          (i) => i.loop?.id === loop.id && i.loop?.index === 0
        )
      )
      .filter((i) => i.length > 0);
    const addConfig: typeof config["add"] = [];
    for (const rows of addRows) {
      const ids = rows.map((row) => row.id);
      addConfig.push({
        loopId: rows[0].loop.id,
        top: getCenterByRowIds(ids),
      });
    }
    const delRows = renderConfig.rows
      .filter((i) => i.loop && i.cells.length > 0)
      .reduce((prev, cur) => {
        const { id, index } = cur.loop;
        const idx = prev.findIndex((i) => i.loopId === id && i.idx === index);
        if (idx !== -1) {
          prev[idx].rowIds.push(cur.id);
        } else {
          prev.push({
            loopId: id,
            rowIds: [cur.id],
            idx: index,
          });
        }
        return prev;
      }, [] as { loopId: string; rowIds: string[]; idx: number }[]);
    const delConfig: typeof config["del"] = [];
    for (const rows of delRows) {
      const ids = rows.rowIds;
      delConfig.push({
        loopId: rows.loopId,
        top: getCenterByRowIds(ids),
        rowIds: rows.rowIds,
        idx: rows.idx,
      });
    }
    setConfig({
      add: addConfig,
      del: delConfig,
    });
  };
  // 给显示监听用
  const generateConfigRef = useLatest(generateGonfig);

  // 设置操作按钮配置
  useEffect(() => {
    if (!enabled || !containerRef.current) return;
    generateConfigRef.current();
    let { height: prevHeight } = containerRef.current.getBoundingClientRect();
    // 容器节点监听高度变化
    const observer = new ResizeObserver(() => {
      const { height: currentHeight } =
        containerRef.current.getBoundingClientRect();
      if (prevHeight !== currentHeight && currentHeight > 0) {
        generateConfigRef.current();
      }
      prevHeight = currentHeight;
    });
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
    };
  }, [enabled, containerRef.current]);

  useEffect(() => {
    onChange?.(config);
  }, [config]);

  return (
    <>
      {enabled && (
        <div
          className="wbuc-pointer-events-none wbuc-absolute wbuc-inset-0 wbuc-size-full"
          ref={actionRef}
        >
          {config.add.map(({ top, loopId }) => (
            <Tooltip
              key={loopId}
              title="手动增加电表"
              getTooltipContainer={() => document.body}
            >
              <Button
                ghost
                icon="＋"
                className={cn("wbuc-left-0", actionBtnClassName)}
                style={{
                  transform: `translate(1.25rem, calc(${top}px - 50%))`,
                  fontSize: 15,
                }}
                type="primary"
                onClick={() => {
                  const loop = tableConfig.loops.find((i) => i.id === loopId)!;
                  const { bindCodes, bindFields } = getLoopMeta(
                    tableConfig.rows,
                    loop,
                    dataSource
                  );
                  const addLoopData = (values = {}) => {
                    onDataSourceChange?.("add", (prev, cb) =>
                      produce(prev, (draft) => {
                        bindCodes.forEach((code) => {
                          const index = draft.findIndex((d) => d.code === code);
                          const d = draft[index];
                          if (index !== -1 && isListValue(d)) {
                            if (!d.valueList) {
                              d.valueList = [];
                            }
                            d.valueList.unshift(values[code] as never);
                            cb(code, values[code]);
                          }
                        });
                      })
                    );
                  };
                  const needInputFields = bindFields
                    .filter((i) => i.type === VALUE_TYPE.TEXT)
                    .map((i) => ({ key: i.code, label: i.name }));
                  if (needInputFields.length > 0) {
                    open(needInputFields).then(addLoopData);
                  } else {
                    addLoopData();
                  }
                }}
              />
            </Tooltip>
          ))}
          {config.del.map(({ top, loopId, rowIds, idx: delIdx }) => (
            <Tooltip
              key={`${loopId}_${delIdx}`}
              getTooltipContainer={() => document.body}
              title="删除电表，不删除物和拓扑，只在结算单生成和修订中删除"
            >
              <Button
                ghost
                icon="—"
                className={cn("wbuc-right-0", actionBtnClassName)}
                style={{
                  transform: `translate(calc(100% - 3.25rem), calc(${top}px - 50%))`,
                  fontSize: 10,
                }}
                danger
                type="primary"
                onClick={() => {
                  Modal.confirm({
                    type: "warn",
                    title: "",
                    content: "确定删除？删除后电表信息将不在单据中展示",
                    onOk: () => {
                      const index = renderConfig.rows.findIndex(
                        (i) => i.id === rowIds[0] && i.loop?.id === loopId
                      );
                      if (index !== -1) {
                        const dataIndex = renderConfig.rows[index].loop?.index;
                        const loop = tableConfig.loops.find(
                          (i) => i.id === loopId
                        )!;
                        const { bindCodes } = getLoopMeta(
                          tableConfig.rows,
                          loop,
                          dataSource
                        );
                        onDataSourceChange?.("remove", (prev, cb) =>
                          produce(prev, (draft) => {
                            bindCodes.forEach((code) => {
                              const i = draft.findIndex((d) => d.code === code);
                              const d = draft[i];
                              if (i !== -1 && isListValue(d)) {
                                d.valueList?.splice(dataIndex, 1);
                                cb(code, dataIndex);
                              }
                            });
                          })
                        );
                      }
                    },
                  });
                }}
              ></Button>
            </Tooltip>
          ))}
        </div>
      )}
      {modal}
    </>
  );
};

export default LoopAction;
